import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CellContext } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';

import DeletePricingPlanFormModal from 'src/features/pricingPlan/components/form/DeletePricingPlanFormModal';
import UpdatePricingPlanFormModal from 'src/features/pricingPlan/components/form/UpdatePricingPlanFormModal';
import { PRICING_PLAN_STATUS, PricingPlan } from 'src/features/pricingPlan/utils/types';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import NavLink from 'src/registry/components/navigation/NavLink';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';

export default function CellPricingPlanActions({ row }: CellContext<PricingPlan, unknown>) {
    const { formatMessage } = useIntl();

    const today = new Date();
    const showEditDeleteActions =
        new Date(row.original.valid_from).getTime() > today.getTime() ||
        row.original.status === PRICING_PLAN_STATUS.DRAFT;

    const managePricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);

    return (
        <div className="flex w-full justify-end gap-1">
            <TooltipProvider>
                {managePricingPlans && showEditDeleteActions && (
                    <>
                        <Tooltip>
                            <UpdatePricingPlanFormModal
                                selectedPricingPlan={row.original}
                                semantics={{ entryName: formatMessage(pricingPlanMessages.pricingPlan).toLowerCase() }}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    </Button>
                                </TooltipTrigger>
                            </UpdatePricingPlanFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.edit} />
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <DeletePricingPlanFormModal
                                selectedPricingPlan={row.original}
                                semantics={{ entryName: formatMessage(pricingPlanMessages.pricingPlan).toLowerCase() }}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    </Button>
                                </TooltipTrigger>
                            </DeletePricingPlanFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.delete} />
                            </TooltipContent>
                        </Tooltip>
                    </>
                )}

                <Tooltip>
                    <TooltipTrigger asChild>
                        <NavLink
                            routerClassName={() => ''}
                            to={`/${PATH.ADMINISTRATION}/pricing-plan/${row.original.id}`}
                        >
                            <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                <FontAwesomeIcon icon={ICON_MAP.EXPAND} />
                            </Button>
                        </NavLink>
                    </TooltipTrigger>
                    <TooltipContent>
                        <FormattedMessage {...pricingPlanMessages.dashboard} />
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}
